import React from 'react';

import PropTypes from 'prop-types';

import {
    Button,
    WDSButton,
    Lightbox,
    Modal,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import events from './events';

import styles from './TripRefreshModal.scss';

class TripRefreshModal extends React.Component {
    static propTypes = {
        direction: PropTypes.oneOf(['depart', 'return', 'summary']),
        doNotDisturb: PropTypes.bool,
        funnelStage: PropTypes.string,
        onPrompt: PropTypes.func,
        onRefresh: PropTypes.func,
        showSkip: PropTypes.bool,
        t: PropTypes.func,
        translationString: PropTypes.string.isRequired,
        tripsExpireAt: PropTypes.number,
    };

    static defaultProps = {
        direction: 'summary',
        doNotDisturb: false,
        onPrompt: () => undefined,
        onRefresh: () => undefined,
    };

    constructor(props) {
        super(props);

        this.state = {
            shouldPrompt: false,
        };

        this.promptTimerId = undefined;
    }

    componentDidMount() {
        this.coordinatePrompt();
    }

    componentDidUpdate(prevProps) {
        const sameTiming = this.props.tripsExpireAt === prevProps.tripsExpireAt;
        if (this.state.shouldPrompt || sameTiming) {
            return;
        }

        this.clearPromptTimer();
        this.coordinatePrompt();
    }

    componentWillUnmount() {
        this.clearPromptTimer();
    }

    coordinatePrompt() {
        if (this.shouldPrompt()) {
            return this.handlePrompt();
        }

        if (this.shouldSchedulePrompt()) {
            this.schedulePrompt();
        }

        return undefined;
    }

    shouldPrompt() {
        const { tripsExpireAt } = this.props;
        return tripsExpireAt && this.getRemainingTime() <= 0;
    }

    shouldSchedulePrompt() {
        const { tripsExpireAt } = this.props;
        return !this.promptTimerId && tripsExpireAt && this.getRemainingTime() > 0;
    }

    getRemainingTime() {
        return this.props.tripsExpireAt - Date.now();
    }

    schedulePrompt() {
        this.clearPromptTimer();

        this.promptTimerId = window.setTimeout(
            () => {
                this.promptTimerId = undefined;
                this.handlePrompt();
            },
            this.getRemainingTime(),
        );
    }

    handlePrompt() {
        events.renderModalEvent({
            direction: this.props.direction,
            funnelStage: this.props.funnelStage,
        });
        this.props.onPrompt();
        this.setState(() => ({ shouldPrompt: true }));
    }

    handleRefresh = () => {
        events.clickRefreshEvent({
            direction: this.props.direction,
            funnelStage: this.props.funnelStage,
        });
        this.clearPromptTimer();
        this.setState(() => ({ shouldPrompt: false }));
        this.props.onRefresh();
    };

    handleSkip = () => {
        events.clickSkipEvent({
            direction: this.props.direction,
            funnelStage: this.props.funnelStage,
        });
        this.clearPromptTimer();
        this.setState(() => ({ shouldPrompt: false }));
    };

    handleClose = () => {
        events.clickScrimEvent({
            direction: this.props.direction,
            funnelStage: this.props.funnelStage,
        });
        this.clearPromptTimer();
        this.setState(() => ({ shouldPrompt: false }));
    };

    clearPromptTimer() {
        if (this.promptTimerId) {
            window.clearTimeout(this.promptTimerId);
            this.promptTimerId = undefined;
        }
    }

    render() {
        const {
            doNotDisturb,
            showSkip,
            translationString,
            t,
        } = this.props;
        const { shouldPrompt } = this.state;

        if (doNotDisturb || !shouldPrompt) {
            return null;
        }

        return (
            <Modal>
                {showSkip
                    ? (
                        <Lightbox
                            innerClassName={styles.container}
                            onHide={this.handleClose}
                            showCloseButton={false}
                        >
                            <Typography className={styles.title} variant="h3">
                                {t('resultsRefreshHeader')}
                            </Typography>
                            <Typography>
                                {t(translationString)}
                            </Typography>
                            <div className={styles.buttonContainer}>
                                <WDSButton onClick={this.handleSkip} size="small" variant="outlined" fullWidth>
                                    {t('skip')}
                                </WDSButton>
                                <WDSButton color="secondary" onClick={this.handleRefresh} size="small" fullWidth>
                                    {t('refresh')}
                                </WDSButton>
                            </div>
                        </Lightbox>
                    )
                    : (
                        <Lightbox
                            innerClassName={styles.container}
                            onHide={this.handleRefresh}
                            showCloseButton={false}
                        >
                            <div className={styles.text}>
                                {t(translationString)}
                            </div>
                            <Button onClick={this.handleRefresh}>
                                {t('refresh')}
                            </Button>
                        </Lightbox>
                    )
                }
            </Modal>
        );
    }
}

export {
    TripRefreshModal,
};

export default localize(TripRefreshModal, ['t'], 'baseApp');
